import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {setProductCounterCount} from 'modules/product-card/thunks';
import {ChangeEvent, useCallback, useContext, useEffect, useMemo} from 'react';
import {useUpdateProductInCart} from 'hooks/product/useUpdateProductInCart';
import {useGetProductInCart} from 'hooks/product/useGetProductInCart';
import {useRemoveProductFromCart} from 'hooks/product/useRemoveProductFromCart';
import {selectProductCounterCount, selectProductData} from 'modules/product-card/selectors';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {actionSetAddedProduct} from 'modules/cart/slice';
import {DEBOUNCE_DELAY_SECOND, MAX_COUNTER_VALUE, MIN_COUNTER_VALUE} from 'core/constants';
import _debounce from 'lodash/debounce';
import {ProductPageContext} from 'modules/product-card/context';
import {useCartEventAnalytics} from 'modules/products/hooks/gtag/useCartEventAnalytics';

export const useProductCounter = (stockAmount?: number) => {
    const dispatch = useAppDispatch();

    const {reviewsTotal} = useContext(ProductPageContext);
    const productData = useAppSelector(selectProductData);

    const cartEventGtag = useCartEventAnalytics();

    const productInCart = useGetProductInCart();
    const {cartData} = productInCart || {};
    const quantity = cartData?.quantity || 0;
    const id = cartData?.id;

    const count = useAppSelector(selectProductCounterCount);
    const {updateCartDebounced} = useUpdateProductInCart({id});
    const removeFromCart = useRemoveProductFromCart({id});

    const removeFromCartDebounced = useMemo(() => _debounce(removeFromCart, DEBOUNCE_DELAY_SECOND), [removeFromCart]);

    const updateValue = useCallback(
        (value: number, cb?: () => void) => {
            if (0 === value) {
                updateCartDebounced.cancel();
                removeFromCartDebounced(cb);
            } else {
                removeFromCartDebounced.cancel();
                updateCartDebounced(value, cb);
            }
        },
        [removeFromCartDebounced, updateCartDebounced],
    );

    const handleChange = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            const {value} = e.target;

            // Если значение пустое, считаем его как 0
            const numberValue = '' === value ? 0 : Number(value);

            if (Number.isNaN(numberValue)) {
                return;
            }

            let resultCount = numberValue;

            const maxCount = stockAmount !== undefined ? Math.min(MAX_COUNTER_VALUE, stockAmount) : MAX_COUNTER_VALUE;
            if (resultCount > maxCount) {
                resultCount = maxCount;
            }

            if (MIN_COUNTER_VALUE > resultCount) {
                resultCount = MIN_COUNTER_VALUE;
            }

            if (resultCount === count) {
                return;
            }

            await dispatch(setProductCounterCount(resultCount));
            updateValue(resultCount, () => {
                if (productData) {
                    cartEventGtag?.(productData, resultCount, undefined, reviewsTotal);
                }
            });
        },
        [stockAmount, count, dispatch, updateValue, cartEventGtag, productData, reviewsTotal],
    );

    const handleDecrement = useCallback(async () => {
        if (count === undefined) {
            return;
        }

        const resultCount = count <= MIN_COUNTER_VALUE ? MIN_COUNTER_VALUE : count - 1;

        await dispatch(setProductCounterCount(resultCount));
        updateValue(resultCount, () => {
            if (productData) {
                cartEventGtag?.(productData, resultCount, undefined, reviewsTotal);
            }
        });
    }, [count, dispatch, updateValue, cartEventGtag, productData, reviewsTotal]);

    const handleIncrement = useCallback(async () => {
        if (count === undefined) {
            return;
        }

        const maxCount = stockAmount !== undefined ? Math.min(MAX_COUNTER_VALUE, stockAmount) : MAX_COUNTER_VALUE;
        if (count >= maxCount) {
            return;
        }

        const resultCount = count + 1;

        await dispatch(setProductCounterCount(resultCount));
        updateValue(resultCount, () => {
            if (productInCart) {
                dispatch(actionSetAddedProduct(productInCart));
            }
            if (productData) {
                cartEventGtag?.(productData, resultCount, undefined, reviewsTotal);
            }
        });
    }, [count, stockAmount, dispatch, updateValue, productInCart, cartEventGtag, productData, reviewsTotal]);

    useEffect(() => {
        dispatch(setProductCounterCount(quantity ?? 0));
    }, [dispatch, quantity]);

    return useMemo(() => {
        return {
            count,
            handleChange,
            handleDecrement,
            handleIncrement,
        };
    }, [count, handleChange, handleDecrement, handleIncrement]);
};
